import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Loader2, ShieldAlert, Download } from "lucide-react";
import { toast } from "sonner";
import { IncidentForm } from '../components/incidents/IncidentForm';
import { IncidentDetail } from '../components/incidents/IncidentDetail';
import { IncidentListItem } from '../components/incidents/IncidentListItem';

export default function EmployeeIncidents() {
  const { authAxios } = useAuth();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  const fetchReports = useCallback(async () => {
    try {
      const res = await authAxios.get('/incident-reports');
      setReports(res.data);
    } finally {
      setLoading(false);
    }
  }, [authAxios]);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8" data-testid="employee-incidents-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-stone-900 font-heading">Incident Reports</h1>
          <p className="text-stone-500 mt-1">Report injuries, near misses and damage. Your admin sees it right away.</p>
        </div>
        <Button onClick={() => setFormOpen(true)} className="bg-rose-600 hover:bg-rose-500 text-white h-12" data-testid="new-incident-btn">
          <Plus className="w-5 h-5 mr-2" /> Report Incident
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-emerald-600" /></div>
      ) : reports.length === 0 ? (
        <div className="text-center py-16 text-stone-400" data-testid="incidents-empty">
          <ShieldAlert className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>You haven't reported any incidents</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="incidents-list">
          {reports.map((r) => <IncidentListItem key={r.id} report={r} onOpen={setSelected} />)}
        </div>
      )}

      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Report an Incident</DialogTitle></DialogHeader>
          {formOpen && <IncidentForm onSubmitted={() => { setFormOpen(false); fetchReports(); }} />}
        </DialogContent>
      </Dialog>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Incident Report</DialogTitle></DialogHeader>
          {selected && (
            <>
              <IncidentDetail report={selected} />
              <div className="flex justify-end mt-4 pt-4 border-t">
                <Button
                  variant="outline"
                  data-testid="employee-incident-pdf-btn"
                  onClick={async () => {
                    try {
                      const res = await authAxios.get(`/pdf/incident-report/${selected.id}`, { responseType: 'blob' });
                      const url = window.URL.createObjectURL(new Blob([res.data]));
                      const link = document.createElement('a');
                      link.href = url;
                      link.setAttribute('download', `incident_report_${selected.id}.pdf`);
                      document.body.appendChild(link);
                      link.click();
                      link.remove();
                      toast.success('PDF downloaded');
                    } catch { toast.error('Failed to export PDF'); }
                  }}
                >
                  <Download className="w-4 h-4 mr-1" /> Download PDF
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
